import React, { FC, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Grid,
} from '@mui/material';
import { LinkedOrganization } from 'models/user';
import { Organization } from 'models/organization';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
const { LAB_ID_KEY } = CONSTANTS;
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type ImpersonateUserOrganizationsDialogProps = {
    handleClose: () => void;
    open: boolean;
    linkedOrganizations: LinkedOrganization[];
    handleConfirm: (organizationId: string, wlLabId: string | undefined) => void;
};

const ImpersonateUserOrganizationsDialog: FC<ImpersonateUserOrganizationsDialogProps> = ({
    handleClose,
    open,
    linkedOrganizations,
    handleConfirm,
}: ImpersonateUserOrganizationsDialogProps) => {
    const { t: translate } = useTranslation(['common']);
    const [selectedOrganization, setSelectedOrganization] = useState({} as Organization);
    const [wlLabId, setWlLabId] = useState<string | undefined>();
    const [showLabsDropDown, setShowLabsDropDown] = useState(false);
    const CurrentLabId = Cookies.get(LAB_ID_KEY);
    const currentOrganization = currentOrgVar();

    useEffect(() => {
        if (open && currentOrganization) {
            const isCurrentOrgLinked = linkedOrganizations?.some(
                (linkedOrganization) =>
                    (linkedOrganization.organization as Organization)._id === currentOrganization._id,
            );
            const initialOrg = isCurrentOrgLinked
                ? currentOrganization
                : (linkedOrganizations[0]?.organization as Organization);
            setSelectedOrganization(initialOrg);
            const showWlCondition = initialOrg?.isWhiteLabeled as boolean;
            setShowLabsDropDown(showWlCondition);
            if (showWlCondition) {
                const isCurrentLabIdValid = initialOrg.connectedLabs?.some(
                    (connectedLab) => !connectedLab.isDisabled && connectedLab.lab?._id === CurrentLabId,
                );
                const firstEnabledLab = initialOrg.connectedLabs?.find((connectedLab) => !connectedLab.isDisabled);
                setWlLabId(isCurrentLabIdValid ? CurrentLabId : firstEnabledLab?.lab?._id);
            }
        }
    }, [open, currentOrganization]);

    const onOrganizationChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        const organization = linkedOrganizations?.find((linkedOrganization) => {
            return (linkedOrganization.organization as Organization)._id === value;
        })?.organization as Organization;
        if (organization) {
            setSelectedOrganization(organization);
            setShowLabsDropDown(organization.isWhiteLabeled as boolean);
            if (organization.isWhiteLabeled) {
                const isCurrentLabIdValid = organization.connectedLabs?.some(
                    (connectedLab) => !connectedLab.isDisabled && connectedLab.lab?._id === CurrentLabId,
                );
                const firstEnabledLab = organization.connectedLabs?.find((connectedLab) => !connectedLab.isDisabled);
                setWlLabId(isCurrentLabIdValid ? CurrentLabId : firstEnabledLab?.lab?._id);
            } else {
                setWlLabId(undefined);
            }
        }
    };

    const onWlLabChange = (event: SelectChangeEvent) => {
        setWlLabId(event.target.value);
    };

    const close = () => {
        setSelectedOrganization({} as Organization);
        setWlLabId(undefined);
        setShowLabsDropDown(false);
        handleClose();
    };

    const isSameOrganization = selectedOrganization._id === currentOrganization._id;
    const isSameLab = wlLabId === CurrentLabId;
    const enabledLabs = useMemo(
        () => selectedOrganization?.connectedLabs?.filter((connectedLab) => !connectedLab.isDisabled),
        [selectedOrganization],
    );

    return (
        <div>
            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle id="alert-dialog-title">{translate('common:ORGANIZATION')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item md={12} sm={12}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel required>{translate('common:ORGANIZATION')}</InputLabel>
                                <Select
                                    value={selectedOrganization?._id || ''}
                                    label={translate('common:ORGANIZATION')}
                                    onChange={onOrganizationChange}
                                >
                                    {linkedOrganizations?.map((linkedOrganization) => {
                                        const organization = linkedOrganization?.organization as Organization;

                                        return (
                                            <MenuItem key={organization?._id} value={organization?._id}>
                                                {currentOrganization?._id === organization?._id
                                                    ? `${organization?.name} (${translate('common:CURRENT')})`
                                                    : organization?.name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        {showLabsDropDown && (
                            <Grid item md={12} sm={12}>
                                <FormControl margin="dense" fullWidth>
                                    <InputLabel required>{translate('common:WHITE_LABEL_LABS')}</InputLabel>
                                    <Select
                                        value={wlLabId}
                                        label={translate('common:WHITE_LABEL_LABS')}
                                        onChange={onWlLabChange}
                                        disabled={!enabledLabs?.length}
                                    >
                                        {enabledLabs?.length > 0 ? (
                                            enabledLabs.map((connectedLab) => (
                                                <MenuItem key={connectedLab?.lab?._id} value={connectedLab?.lab?._id}>
                                                    {connectedLab?.lab?.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>{translate('common:NO_LABS_AVAILABLE')}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>{translate('common:CANCEL')}</Button>
                    <Button
                        disabled={
                            !selectedOrganization._id ||
                            (showLabsDropDown && !wlLabId) ||
                            (isSameOrganization && showLabsDropDown && isSameLab)
                        }
                        onClick={() => handleConfirm(selectedOrganization._id, wlLabId)}
                        variant="contained"
                        autoFocus
                    >
                        {translate('common:CONFIRM')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ImpersonateUserOrganizationsDialog;
